import { MuiThemeProvider, CssBaseline, makeStyles } from "@material-ui/core";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { RootState } from "../../store/rootReducer";
import themeDashboard from "../shared/themeDashboard";
import { showNavbar } from "../../store/modules/appStateSlice";
import Paper from "@material-ui/core/Paper";
import { DATE_MAP, getRoles } from "./helpers/dashboardHelpers";
import CustomDateHeader from "./helpers/CustomDateHeader";
import SubHeader from "./helpers/SubHeader";
import { filterLocationForPopup, getLocationsDataForPopup } from "../popup/hospitalPopupHelpers";
import HospitalPopup from "../popup/HospitalPopup";
import ISelectItem from "../popup/types/ISelectItem";
import { IAppPropsWithRoute } from "../../common/types";
import PatientTransportDashboardWidget from "./patientTransport/PatientTransportDashboardWidget";
import DashboardSwitch from "./DashboardSwitch";
import AgedCareDashboardWidget from "./agedCare/AgedCareDashboardWidget";
import api from "../../store/api";
import IAuthState from "../../interfaces/IAuthState";
import { IUserType } from "./types/dashboardTypes";
import Navbar from "../shared/Navbar";

interface DashboardProps extends IAppPropsWithRoute {}

const patientTransportDashboardName = "patientTransport";
const agedCareDashboardName = "agedCare";

const getDashboardName = (isPatienTransportAvailable: boolean, isAgedCareAvailable: boolean): string => {
  if (isPatienTransportAvailable) return patientTransportDashboardName;
  if (isAgedCareAvailable) return agedCareDashboardName;
  return "";
};

const initSelectedLocations = (authStore: IAuthState, userRoles: IUserType): string[] => {
  if (userRoles.IsAdmin || userRoles.CanViewDashboardAsSMA) {
    return [];
  }
  return [authStore.data?.location ?? ""];
};

const Dashboard: React.FC<DashboardProps> = () => {
  const { authStore } = useSelector((state: RootState) => state);
  const isAgedCareAvailable = authStore.hasAgedCareLocations ?? false;
  const isPatienTransportAvailable = authStore.hasTransportLocations ?? false;
  const userRoles = getRoles(authStore);

  const dispatch = useDispatch();
  const [startDate, setStartDate] = React.useState<any>(format(new Date().setDate(new Date().getDate() - 29), "yyyy-MM-dd"));
  const [endDate, setEndDate] = React.useState<any>(format(new Date(), "yyyy-MM-dd"));
  const popState = React.useState(false);
  const locationState = React.useState<ISelectItem[]>([]);
  const [selectedHospital, setSelectedHospital] = React.useState<any>("All Hospitals");
  const [selectedLocations, setSelectedLocations] = React.useState<string[]>(initSelectedLocations(authStore, userRoles));

  const [dahsboardName, setDahsboardName] = React.useState<string>(getDashboardName(isPatienTransportAvailable, isAgedCareAvailable));
  const [isSwitchVisible, setIsSwitchVisible] = React.useState<boolean>((isPatienTransportAvailable === isAgedCareAvailable) === true);

  const [activeOption, setActiveOption] = React.useState<string>("last30days");
  const initialMount = React.useRef(true);

  const filterLocation = () => {
    filterLocationForPopup(locationState, setSelectedHospital, setSelectedLocations, null);
  };

  const getLocationsData = async (): Promise<void> => {
    await getLocationsDataForPopup(locationState, setSelectedHospital, setSelectedLocations, selectedLocations, null);
  };

  const checkDashboardLocationForSwitchVisibility = async (): Promise<void> => {
    try {
      const res = await api.hasDashboardLocations("", { locationIds: selectedLocations });
      if (res.errorRaised) throw res.errorMessage;

      const shouldSwitchBeVisible = res.model.hasAgedCareLocations && res.model.hasTransportLocations;
      setIsSwitchVisible(shouldSwitchBeVisible);
      if (!shouldSwitchBeVisible) {
        setDahsboardName(getDashboardName(res.model.hasTransportLocations, res.model.hasAgedCareLocations));
      }
    } catch (error) {
      console.error(error);
      setIsSwitchVisible(false);
    }
  };

  React.useEffect(() => {
    dispatch(showNavbar(true));
    getLocationsData();
  }, []);

  React.useEffect(() => {
    if (userRoles.IsAdmin || userRoles.CanViewDashboardAsSMA) {
      setIsSwitchVisible(true);
    } else if (selectedLocations?.length) {
      checkDashboardLocationForSwitchVisibility();
    }
  }, [selectedLocations]);

  React.useEffect(() => {
    if (activeOption != "custom") {
      if (initialMount.current) {
        initialMount.current = false;
      } else {
        setStartDate(format(DATE_MAP[activeOption][0], "yyyy-MM-dd"));
        setEndDate(format(DATE_MAP[activeOption][1], "yyyy-MM-dd"));
      }
    }
  }, [activeOption]);

  const chooseDashboard = () => {
    switch (dahsboardName) {
      case patientTransportDashboardName:
        return (
          <PatientTransportDashboardWidget
            startDate={startDate}
            endDate={endDate}
            selectedLocations={selectedLocations}
            authStore={authStore}
            userRoles={userRoles}
          />
        );
      case agedCareDashboardName:
        return (
          <AgedCareDashboardWidget
            startDate={startDate}
            endDate={endDate}
            selectedLocations={selectedLocations}
            authStore={authStore}
            userRoles={userRoles}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <MuiThemeProvider theme={themeDashboard}>
        <CssBaseline />
        <div style={{ backgroundColor: "#00152E", position: "fixed", width: "100%", height: "162px", top: "59px", zIndex: 1200 }}>
          <SubHeader
            leftButtonClick={() => popState[1](true)}
            title={selectedHospital}
            activeOption={activeOption}
            setActiveOption={setActiveOption}
            authStore={authStore}
          />
          {activeOption == "custom" && <CustomDateHeader setEndDateMain={setEndDate} setStartDateMain={setStartDate} />}
          {isSwitchVisible && (
            <DashboardSwitch
              dahsboardName={dahsboardName}
              setDahsboardName={setDahsboardName}
              agedCareDashboardName={agedCareDashboardName}
              patientTransportDashboardName={patientTransportDashboardName}
            />
          )}
        </div>
        <Paper style={{ marginTop: "221px" }}>{chooseDashboard()}</Paper>
        <HospitalPopup popState={popState} filterLocation={filterLocation} locationState={locationState} />
      </MuiThemeProvider>
    </>
  );
};

export default Dashboard;
